import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  Mail,
  Phone,
  FileText,
  Palette,
  Sparkles,
  Bell,
  BellOff,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { plannedActivitiesApi } from "../../lib/plannedActivitiesApi";
import TimeSlotPicker from "./TimeSlotPicker";
import AssigneeMultiPicker from "./AssigneeMultiPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { nameInitials } from "../../lib/nameInitials";

const ACTIVITY_TYPES = [
  { key: "email",  label: "E-posta",  Icon: Mail,     color: "bg-blue-100 text-blue-700 border-blue-300" },
  { key: "phone",  label: "Telefon",  Icon: Phone,    color: "bg-sky-100 text-sky-700 border-sky-300" },
  { key: "quote",  label: "Teklif",   Icon: FileText, color: "bg-amber-100 text-amber-700 border-amber-300" },
  { key: "design", label: "Tasarım",  Icon: Palette,  color: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-300" },
  { key: "custom", label: "Özel",     Icon: Sparkles, color: "bg-zinc-100 text-zinc-700 border-zinc-300" },
];

const REMIND_BEFORE = [
  { key: 10,   label: "10 dk önce" },
  { key: 30,   label: "30 dk önce" },
  { key: 60,   label: "1 saat önce" },
  { key: 180,  label: "3 saat önce" },
  { key: 1440, label: "1 gün önce" },
];

const QUICK_DATES = [
  { key: 0, label: "Bugün" },
  { key: 1, label: "Yarın" },
  { key: 3, label: "3 Gün" },
  { key: 7, label: "1 Hafta" },
];

/**
 * Aktivite Planlama dialog — schedule a future task tied to a
 * construction row. Mirrors the ExproCRM design:
 *   - 5 activity type icons (email / phone / quote / design / custom)
 *   - Title + Date + Time
 *   - Description
 *   - Priority radio
 *   - Quick-date chips (Bugün / Yarın / 3 Gün / 1 Hafta)
 *   - Reminder toggle with expandable "when + how" settings
 */
export default function ActivityPlanDialog({
  open,
  onClose,
  constructionId,
  users,
  currentUserId,
  onCreated,
}) {
  const [type, setType] = useState("phone");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("09:00");
  const [priority, setPriority] = useState("normal");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [remindEnabled, setRemindEnabled] = useState(true);
  const [remindBefore, setRemindBefore] = useState(60);
  const [channels, setChannels] = useState(["system"]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType("phone");
    setTitle("");
    setDescription("");
    // Default: today, 09:00
    const today = new Date();
    setDate(today.toISOString().slice(0, 10));
    setTime("09:00");
    setPriority("normal");
    setAssigneeIds(currentUserId ? [currentUserId] : []);
    setRemindEnabled(true);
    setRemindBefore(60);
    setChannels(["system"]);
  }, [open, currentUserId]);

  const setQuickDate = (daysAhead) => {
    const d = new Date();
    d.setDate(d.getDate() + daysAhead);
    setDate(d.toISOString().slice(0, 10));
  };

  const toggleChannel = (key) => {
    setChannels((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  };

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error("Başlık zorunlu");
      return;
    }
    if (!date || !time) {
      toast.error("Tarih ve saat zorunlu");
      return;
    }
    // Guard against past dates — planned activities must be scheduled
    // for now or later. The <input min> attribute already blocks most
    // interactions but users can still paste an old value, so we
    // double-check on submit.
    const scheduledAt = new Date(`${date}T${time}:00`);
    if (scheduledAt.getTime() < Date.now() - 60_000) {
      toast.error("Planlı aktivite geçmiş tarihe atılamaz");
      return;
    }
    setSaving(true);
    try {
      const doc = await plannedActivitiesApi.create(constructionId, {
        kind: "planned",
        activity_type: type,
        title: trimmedTitle,
        description,
        scheduled_at: scheduledAt.toISOString(),
        priority,
        assigned_user_ids: assigneeIds,
        reminder_enabled: remindEnabled,
        reminder_before_minutes: remindEnabled ? remindBefore : null,
        reminder_channels: remindEnabled ? channels : [],
      });
      toast.success("Aktivite planlandı");
      onCreated?.(doc);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Planlanamadı");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[92vh] overflow-y-auto"
        data-testid="activity-plan-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CalendarIcon className="h-4 w-4" />
            </span>
            Aktivite Planlama
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Aktivite Tipi */}
          <div>
            <Label className="mb-2 block text-xs font-semibold text-zinc-700">
              Aktivite Tipi
            </Label>
            <div className="grid grid-cols-5 gap-2">
              {ACTIVITY_TYPES.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setType(t.key)}
                  data-testid={`plan-type-${t.key}`}
                  className={`relative flex flex-col items-center gap-1 rounded-lg border p-3 text-xs font-medium transition-all ${
                    type === t.key
                      ? `${t.color} shadow-sm`
                      : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
                  }`}
                >
                  <t.Icon className="h-4 w-4" />
                  {t.label}
                  {type === t.key && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-white shadow">
                      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5">
                        <path
                          d="M2 6.5L5 9L10 3"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="sm:col-span-3">
              <Label htmlFor="plan-title" className="text-xs font-semibold text-zinc-700">
                Başlık *
              </Label>
              <Input
                id="plan-title"
                placeholder="Aktivite başlığı"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="plan-title-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="plan-date" className="text-xs font-semibold text-zinc-700">
                Tarih *
              </Label>
              <Input
                id="plan-date"
                type="date"
                value={date}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDate(e.target.value)}
                data-testid="plan-date-input"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">
                Saat
              </Label>
              <div className="mt-1">
                <TimeSlotPicker value={time} onChange={setTime} testId="plan-time" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-zinc-700">
                Öncelik
              </Label>
              <div className="mt-1 inline-flex rounded-lg border border-zinc-200 bg-white p-0.5">
                {[
                  { k: "high",   label: "Yüksek", tone: "text-red-700 bg-red-50" },
                  { k: "normal", label: "Normal", tone: "text-blue-700 bg-blue-50" },
                  { k: "low",    label: "Düşük",  tone: "text-zinc-700 bg-zinc-50" },
                ].map(({ k, label, tone }) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setPriority(k)}
                    data-testid={`plan-priority-${k}`}
                    className={`rounded-md px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      priority === k ? tone : "text-zinc-500 hover:bg-zinc-50"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Açıklama */}
          <div>
            <Label htmlFor="plan-desc" className="text-xs font-semibold text-zinc-700">
              Açıklama
            </Label>
            <Textarea
              id="plan-desc"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ek bilgiler..."
              data-testid="plan-desc-input"
              className="mt-1"
            />
          </div>

          {/* Sorumlu — who gets the reminder (multi) */}
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              Sorumlu (kime atansın?)
            </Label>
            <div className="mt-1">
              <AssigneeMultiPicker
                value={assigneeIds}
                onChange={setAssigneeIds}
                users={users}
                testId="plan-assignee"
                placeholder="Sorumlu atanmadı"
              />
            </div>
            <p className="mt-1 text-[10px] text-zinc-500">
              Hatırlatma seçilen kişilerin tümüne gönderilir.
            </p>
          </div>

          {/* Quick date chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-zinc-500">Hızlı tarih:</span>
            {QUICK_DATES.map((q) => (
              <button
                key={q.key}
                type="button"
                onClick={() => setQuickDate(q.key)}
                data-testid={`plan-quick-date-${q.key}`}
                className="rounded-full border border-zinc-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-zinc-600 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
              >
                {q.label}
              </button>
            ))}
          </div>

          {/* Reminder */}
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {remindEnabled ? (
                  <Bell className="h-4 w-4 text-amber-600" />
                ) : (
                  <BellOff className="h-4 w-4 text-zinc-400" />
                )}
                <span className="text-sm font-semibold text-zinc-800">
                  Hatırlat
                </span>
              </div>
              <Switch
                checked={remindEnabled}
                onCheckedChange={setRemindEnabled}
                data-testid="plan-remind-toggle"
              />
            </div>

            {remindEnabled && (
              <div className="mt-3 space-y-3 border-t border-zinc-200 pt-3">
                <div>
                  <Label className="mb-1.5 block text-[11px] font-semibold text-zinc-600">
                    Ne zaman hatırlatılsın?
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {REMIND_BEFORE.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setRemindBefore(r.key)}
                        data-testid={`plan-remind-before-${r.key}`}
                        className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                          remindBefore === r.key
                            ? "border-amber-400 bg-amber-100 text-amber-800"
                            : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50"
                        }`}
                      >
                        {r.label}
                        {remindBefore === r.key && " ✓"}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block text-[11px] font-semibold text-zinc-600">
                    Nasıl hatırlatılsın?
                  </Label>
                  <div className="space-y-1.5">
                    <ChannelRow
                      k="system"
                      title="Sistem Bildirimi"
                      subtitle="Uygulama içi bildirim"
                      tag="Ücretsiz"
                      tagTone="bg-emerald-100 text-emerald-800"
                      checked={channels.includes("system")}
                      onToggle={() => toggleChannel("system")}
                    />
                    <ChannelRow
                      k="email"
                      title="E-posta"
                      subtitle="Mock Outbox'a düşer (kayıtlı e-posta adresine)"
                      tag="Ücretsiz"
                      tagTone="bg-emerald-100 text-emerald-800"
                      checked={channels.includes("email")}
                      onToggle={() => toggleChannel("email")}
                    />
                    <ChannelRow
                      k="sms"
                      title="SMS"
                      subtitle="Yakında — Twilio entegrasyonu gerekiyor"
                      tag="Ek ücret"
                      tagTone="bg-amber-100 text-amber-800"
                      checked={false}
                      onToggle={() =>
                        toast.info("SMS hatırlatma yakında eklenecek")
                      }
                      disabled
                    />
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500">
                  Hatırlatma, aktivite tarih ve saatinden belirtilen süre kadar önce gönderilecektir.
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="plan-cancel"
          >
            <X className="mr-1 h-4 w-4" /> İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={saving}
            data-testid="plan-submit"
            className="bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <CalendarIcon className="mr-1 h-4 w-4" /> Planla
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChannelRow({ k, title, subtitle, tag, tagTone, checked, onToggle, disabled }) {
  return (
    <label
      className={`flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm ${
        disabled ? "opacity-60" : "cursor-pointer hover:border-zinc-300"
      }`}
    >
      <div className="flex items-center gap-3">
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          disabled={disabled}
          data-testid={`plan-channel-${k}`}
        />
        <div>
          <div className="font-medium text-zinc-800">{title}</div>
          <div className="text-[11px] text-zinc-500">{subtitle}</div>
        </div>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tagTone}`}>
        {tag}
      </span>
    </label>
  );
}
